import React from 'react';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import {
  DataLakeType,
  DEFAULT_LOGS_DB_NAME_DATABRICKS,
  DEFAULT_LOGS_DB_NAME_SNOWFLAKE,
  DEFAULT_MAX_PAGE_SIZE,
} from 'Source/constants';
import { SavedQueryDetails } from 'Source/graphql/fragments/SavedQueryDetails.generated';
import { DataLakeQuerySortDirection } from 'Generated/schema';
import { useAppConfig } from 'Components/utils/AppBootstrapper/AppConfigContext';
import { Filters, UnifiedFormValues } from './types';
import { TimeFrameEnum } from './CrossLogSearch/types';

type State = {
  formState: UnifiedFormValues;
  savedQueryDetails: SavedQueryDetails;
};

type Action<T, P = never> = {
  type: T;
  payload?: P;
};

type ResetFormValues = Action<'RESET_FORM_VALUES', UnifiedFormValues>;
type UpdateFormValues = Action<'UPDATE_FORM_VALUES', UnifiedFormValues>;
type UpdateSavedQueryDetails = Action<'UPDATE_SAVED_QUERY_DETAILS', SavedQueryDetails>;

type Actions = ResetFormValues | UpdateFormValues | UpdateSavedQueryDetails;

const queryBuilderStateReducer = (state: State, action: Actions) => {
  switch (action.type) {
    case 'RESET_FORM_VALUES': {
      return {
        formState: action.payload,
        savedQueryDetails: null,
      };
    }
    case 'UPDATE_FORM_VALUES': {
      return {
        ...state,
        formState: action.payload,
      };
    }
    case 'UPDATE_SAVED_QUERY_DETAILS': {
      return {
        ...state,
        savedQueryDetails: action.payload,
      };
    }
    default: {
      return state;
    }
  }
};

export const createInitialFiltersState = (): Filters => ({
  ors: [
    {
      uuid: uuidv4(),
      ands: [],
    },
  ],
});

export const getInitialPantherFlowInput = (dataLakeType?: DataLakeType) => {
  const db = dataLakeType === DataLakeType.Databricks ? 'panther_logs' : 'panther_logs.public';
  return `union ${db}.*\n| where p_event_time > time.ago(20m)`;
};

export const getInitialFormValues = (dataLakeType?: DataLakeType) => {
  const now = dayjs.utc();
  const formValues = {
    startTime: now.subtract(20, 'minutes'),
    endTime: now,
    filters: createInitialFiltersState(),
    pantherFlowInput: getInitialPantherFlowInput(dataLakeType),
    limit: DEFAULT_MAX_PAGE_SIZE,
    sortDirection: DataLakeQuerySortDirection.Desc,
    usePantherFlow: false,
  };
  return {
    ...formValues,
    databases: [
      dataLakeType === DataLakeType.Databricks
        ? DEFAULT_LOGS_DB_NAME_DATABRICKS
        : DEFAULT_LOGS_DB_NAME_SNOWFLAKE,
    ],
    tables: [],
    timeFrame: TimeFrameEnum.Last20Minutes,
  };
};

export function useQueryBuilderStateHandler() {
  const { pantherDataLakeType } = useAppConfig();
  const initialValues = {
    formState: getInitialFormValues(pantherDataLakeType as DataLakeType),
    savedQueryDetails: null,
  };

  const [state, dispatch] = React.useReducer(queryBuilderStateReducer, {
    ...initialValues,
  });

  return React.useMemo(() => {
    return {
      state,
      resetFormValues: (values: UnifiedFormValues) => {
        dispatch({
          type: 'RESET_FORM_VALUES',
          payload: values,
        });
      },
      updateFormValues: (values: UnifiedFormValues) => {
        dispatch({
          type: 'UPDATE_FORM_VALUES',
          payload: values,
        });
      },
      updateSavedQueryDetails: (values: SavedQueryDetails) => {
        dispatch({
          type: 'UPDATE_SAVED_QUERY_DETAILS',
          payload: values,
        });
      },
    };
  }, [state, dispatch]);
}
